import type { ActivationPendingOrganizationResponse } from '@acme/api/admin';
import { OrganizationService as AdminOrganizationService } from '@acme/api/admin';
import type { OrganizationFormDefaultData } from '@acme/features';
import { OrganizationGeneralForm } from '@acme/features';
import { Breadcrumb } from '@acme/foundation';
import { OrganizationChecks } from '@widgets/organizationChecks';
import { OrganizationHistory } from '@widgets/organizationHistory';
import { Badge, Button, Card, Spinner } from 'flowbite-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

type LocationState = {
  organization?: ActivationPendingOrganizationResponse;
};

const toFormDefaultData = (
  org: ActivationPendingOrganizationResponse | null
): OrganizationFormDefaultData => ({
  name: org?.name ?? null,
  inn: org?.inn ?? null,
  ogrn: org?.orgn ?? null,
  legalAddress: null,
  actualAddress: null,
  phoneNumber: null,
  responsiblePersonName: null,
  bankDetail: null,
  role: null,
});

export function OrganizationDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? null;

  const [organization, setOrganization] =
    useState<ActivationPendingOrganizationResponse | null>(
      state?.organization ?? null
    );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    if (organization?.id === id) return;

    let isActive = true;
    (async () => {
      setIsLoading(true);
      try {
        const response =
          await AdminOrganizationService.getApiAdminV1OrganizationPending();
        const found = response.data?.find((o) => o.id === id) ?? null;
        if (isActive) setOrganization(found);
      } finally {
        if (isActive) setIsLoading(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [id]);

  const breadcrumbItems = useMemo(
    () => [
      {
        label: 'Организации',
        onClick: () => navigate('/organizations/pending'),
      },
      { label: organization?.name ?? 'Детали организации' },
    ],
    [navigate, organization?.name]
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <Breadcrumb items={breadcrumbItems} />
        <Button color="purple" outline disabled>
          Редактировать
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-10">
            <Spinner size="lg" />
          </div>
        </Card>
      ) : (
        <OrganizationGeneralForm
          isEdit={false}
          defaultData={toFormDefaultData(organization)}
          headerExtra={<Badge color="warning">Новая</Badge>}
        />
      )}

      <OrganizationChecks />
      <OrganizationHistory />
    </div>
  );
}
